import { GQL } from "@binale-tech/shared";
import { useApolloClient } from "@apollo/client/react";
import { use, useRef, useState } from "react";
import { uuidv7 } from "uuidv7";

import { useDmsAutoSort } from "@dms/hooks/useDmsAutoSort";
import { useDmsContactActions } from "@dms/hooks/useDmsContactActions";
import { BanksContext, BankTransactionsContext } from "@domain/context/BanksContext";
import { AllRecordsContext, RecordsBankContext } from "@domain/context/accountingData/RecordsCtx";
import { CashRegisterContext, CashRegisterTransactionsContext } from "@domain/context/CashRegisterContext";
import { CompanyAccountingYearContext, CompanyContext, YearPeriodContext } from "@domain/context/CompanyContext";
import { DmsTypesContext, useDmsDocumentsMap } from "@domain/context/DmsDataContexts";
import { transactionsExportToRecords } from "@domain/context/mutations/bankMutations.graphql";
import { cashRegisterTransactionsCreateFromDocuments } from "@domain/context/mutations/cashRegisterMutations.graphql";
import { genericRecordsAiReview } from "@domain/context/mutations/genericRecordMutations.graphql";
import {
    postingRuleImportSuggestionsMutation,
    postingRuleSuggestionsQuery,
} from "@domain/context/queries/postingRules.graphql";
import {
    accountReconciliationApply,
    bankingFillBank,
    bankingTransactionsCreateContacts,
    bankingTransactionsMatchContacts,
} from "@domain/context/mutations/bankMutations.graphql";
import {
    accountReconciliationAccounts,
    accountReconciliationPlan,
    bankingTransactionsContactSuggestions,
} from "@domain/context/queries/bankQueries.graphql";
import {
    elsterExportsAgentQuery,
    dmsDocumentFindTotalQuery,
    documentsProcessAiMutation,
    pendingWorkQuery,
    recordsAiReviewQuery,
} from "@domain/context/queries/agentQueries.graphql";
import { UserContext } from "@domain/context/UserProvider";
import { mutationDmsDocumentsToRecordsEnqueue, queryAutomationJob } from "@domain/graphql/automationJob";
import { DmsUtils } from "@dms/scripts/utils/DmsUtils";
import { DmsType } from "@domain/models/dms";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { mailboxConfigQuery } from "@dms/features/mailbox/mailbox.graphql";

import { resolveUstvaFrequency } from "./ustvaFrequency";
import { MAX_IDS_PER_JOB } from "./steps/draftSelection";
import { exportedTransactionIds } from "./steps/exportedTransactions";
import { sendInChunks } from "./steps/paging";
import { buildCashRegisterFolders } from "./steps/cashRegisterFolders";
import { readInChunks } from "./steps/chunks";
import { findDraftDocuments } from "./steps/findDraftDocuments";
import type { AgentContext } from "./runner/types";

/**
 * Builds the context of one modal opening. The object is created once; live inputs (company, year, rights, documents,
 * banks, mutations) are read through refs, so a step always sees the current value without a re-created context.
 */
export const useAgentContext = (): AgentContext => {
    const { companyGQL } = use(CompanyContext);
    const { year } = use(YearPeriodContext);
    const user = use(UserContext);
    const { banks } = use(BanksContext);
    const documentsMap = useDmsDocumentsMap();
    const client = useApolloClient();
    const autoSort = useDmsAutoSort();
    const { fillContacts, createContacts } = useDmsContactActions();
    const { documentTypes } = use(DmsTypesContext);
    const { registersById } = use(CashRegisterContext);
    const { transactionMap, transactionRecordsMap } = use(BankTransactionsContext);
    const { transactionMap: kasseTransactionMap, transactionRecordsMap: kasseRecordsMap } = use(
        CashRegisterTransactionsContext,
    );
    const recordsBank = use(RecordsBankContext);
    const allRecords = use(AllRecordsContext);
    const { yearConfig } = use(CompanyAccountingYearContext);

    const live = {
        companyGQL,
        year,
        user,
        banks,
        documentsMap,
        client,
        autoSort,
        fillContacts,
        createContacts,
        documentTypes,
        registersById,
        transactionMap,
        transactionRecordsMap,
        kasseTransactionMap,
        kasseRecordsMap,
        recordsBank,
        allRecords,
        yearConfig,
    };
    const liveRef = useRef(live);
    liveRef.current = live;

    const [context] = useState((): AgentContext => {
        const companyId = () => liveRef.current.companyGQL?.id ?? "";
        return {
            // companyId and year are fixed at open time; the modal is remounted per opening
            companyId: companyId(),
            year: liveRef.current.year,
            canWrite: (p) => ProductAccessUtils.canWrite(p, liveRef.current.companyGQL, liveRef.current.user),
            canRead: (p) => ProductAccessUtils.canRead(p, liveRef.current.companyGQL, liveRef.current.user),
            created: { recordIds: [], documentIds: [] },
            documents: () => liveRef.current.documentsMap,
            banks: () => liveRef.current.banks,
            readPendingBuckets: async () => {
                const { data } = await liveRef.current.client.query({
                    query: pendingWorkQuery,
                    variables: { companyId: companyId(), year: liveRef.current.year },
                    fetchPolicy: "no-cache",
                });
                return data?.pendingWork.buckets ?? [];
            },
            countNewDocuments: async () => {
                const { data } = await liveRef.current.client.query({
                    query: dmsDocumentFindTotalQuery,
                    variables: { input: { companyId: companyId(), types: [DmsType.new_documents] } },
                    fetchPolicy: "no-cache",
                });
                return data?.dmsDocumentFind.total ?? 0;
            },
            readMailboxConfig: async () => {
                const { data } = await liveRef.current.client.query({
                    query: mailboxConfigQuery,
                    variables: { companyId: companyId() },
                    fetchPolicy: "no-cache",
                });
                return data?.mailboxConfig ?? null;
            },
            processAi: async (ids) => {
                await liveRef.current.client.mutate({
                    mutation: documentsProcessAiMutation,
                    variables: { input: { companyId: companyId(), ids } },
                });
            },
            autoSort: (ids) => liveRef.current.autoSort(ids),
            uploadRequested: false,
            autoSortResults: [],
            fillDocumentContacts: (ids) => liveRef.current.fillContacts(ids),
            createDocumentContacts: (ids) => liveRef.current.createContacts(ids),
            cashRegisterFolders: () =>
                buildCashRegisterFolders(liveRef.current.documentTypes, liveRef.current.registersById),
            createKasseFromDocuments: async (cashRegisterId, documentIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: cashRegisterTransactionsCreateFromDocuments,
                    variables: { companyId: companyId(), cashRegisterId, documentIds },
                });
                return data?.cashRegisterTransactionsCreateFromDocuments ?? [];
            },
            bankTransactions: () => [...liveRef.current.transactionMap.values()],
            matchBankContacts: async (transactionIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: bankingTransactionsMatchContacts,
                    variables: { companyId: companyId(), transactionIds },
                });
                return data?.bankingTransactionsMatchContacts ?? [];
            },
            readBankContactSuggestions: async (transactionIds) => {
                const { data } = await liveRef.current.client.query({
                    query: bankingTransactionsContactSuggestions,
                    variables: { companyId: companyId(), transactionIds },
                    fetchPolicy: "no-cache",
                });
                return data?.bankingTransactionsContactSuggestions ?? [];
            },
            createBankContacts: async (vatIds, transactionIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: bankingTransactionsCreateContacts,
                    variables: { companyId: companyId(), vatIds, transactionIds },
                });
                return data?.bankingTransactionsCreateContacts ?? [];
            },
            findDraftDocuments: () => findDraftDocuments(liveRef.current.client, companyId(), liveRef.current.year),
            documentGroupId: (type, subType) =>
                subType ? DmsUtils.getGroupId(type, subType, liveRef.current.documentTypes) : undefined,
            enqueueDocumentsToRecords: async (productKey, groupId, documentIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: mutationDmsDocumentsToRecordsEnqueue,
                    variables: { input: { companyId: companyId(), documentIds, productKey, groupId: groupId ?? null } },
                });
                const job = data?.dmsDocumentsToRecordsEnqueue;
                if (!job) throw new Error("dmsDocumentsToRecordsEnqueue returned no job");
                return job.id;
            },
            readAutomationJob: async (id) => {
                const { data } = await liveRef.current.client.query({
                    query: queryAutomationJob,
                    variables: { companyId: companyId(), id },
                    fetchPolicy: "no-cache",
                });
                if (!data?.automationJob) throw new Error("Job not found");
                return data.automationJob;
            },
            cashRegisterTransactions: () => [...liveRef.current.kasseTransactionMap.values()],
            exportedTransactionIds: (kind) =>
                exportedTransactionIds(
                    kind,
                    kind === GQL.ITransactionExportKind.Bank
                        ? [...liveRef.current.transactionRecordsMap.values()].flat()
                        : liveRef.current.kasseRecordsMap.values(),
                ),
            exportTransactions: (kind, ids) =>
                sendInChunks(ids, MAX_IDS_PER_JOB, async (transactionIds) => {
                    const { data } = await liveRef.current.client.mutate({
                        mutation: transactionsExportToRecords,
                        variables: { companyId: companyId(), kind, transactionIds },
                    });
                    return data?.transactionsExportToRecords ?? [];
                }),
            bankRecords: () => liveRef.current.recordsBank.combinedList,
            fillBank: async (action, recordKeys) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: bankingFillBank,
                    variables: { companyId: companyId(), action, recordKeys },
                });
                return data?.bankingFillBank ?? [];
            },
            readReconciliationAccounts: async () => {
                const { data } = await liveRef.current.client.query({
                    query: accountReconciliationAccounts,
                    variables: { companyId: companyId(), year: liveRef.current.year },
                    fetchPolicy: "no-cache",
                });
                return data?.accountReconciliationAccounts ?? [];
            },
            planReconciliation: async (konto) => {
                const { data } = await liveRef.current.client.query({
                    query: accountReconciliationPlan,
                    variables: { companyId: companyId(), konto, mode: GQL.IReconciliationMode.Quick, key: null },
                    fetchPolicy: "no-cache",
                });
                if (!data) throw new Error("accountReconciliationPlan returned no data");
                return data.accountReconciliationPlan;
            },
            applyReconciliation: async (konto, groupIds) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: accountReconciliationApply,
                    variables: {
                        companyId: companyId(),
                        konto,
                        mode: GQL.IReconciliationMode.Quick,
                        key: null,
                        groupIds,
                        idempotencyKey: uuidv7(),
                    },
                });
                return data?.accountReconciliationApply ?? [];
            },
            recordInfo: (id) => {
                const record = liveRef.current.allRecords.map.get(id);
                const product = record?.getProductKey();
                return product ? { product } : undefined;
            },
            runAiReview: async (product, ids) => {
                await liveRef.current.client.mutate({
                    mutation: genericRecordsAiReview,
                    variables: { input: { companyId: companyId(), product, ids } },
                });
            },
            readAiReviews: (product, ids) =>
                readInChunks(ids, async (chunk, limit) => {
                    const { data } = await liveRef.current.client.query({
                        query: recordsAiReviewQuery,
                        variables: {
                            companyId: companyId(),
                            product,
                            year: liveRef.current.year,
                            ids: chunk,
                            limit,
                        },
                        fetchPolicy: "no-cache",
                    });
                    return (data?.records.records ?? []).map((r) => ({
                        id: String(r.id),
                        aiReview: r.state.aiReview ?? null,
                    }));
                }),
            readPostingRuleSuggestionCount: async (direction) => {
                const { data } = await liveRef.current.client.query({
                    query: postingRuleSuggestionsQuery,
                    variables: { companyId: companyId(), direction },
                    fetchPolicy: "no-cache",
                });
                return data?.postingRuleSuggestions.length ?? 0;
            },
            runReport: { steps: {}, notRun: [] },
            readElsterExports: async () => {
                const { data } = await liveRef.current.client.query({
                    query: elsterExportsAgentQuery,
                    variables: { companyId: companyId(), year: liveRef.current.year },
                    fetchPolicy: "no-cache",
                });
                return data?.elsterExports ?? [];
            },
            ustvaFrequency: () => resolveUstvaFrequency(liveRef.current.yearConfig),
            ustvaDauerfrist: () => liveRef.current.yearConfig?.dauerfristverlaengerung === true,
            importPostingRuleSuggestions: async (direction) => {
                const { data } = await liveRef.current.client.mutate({
                    mutation: postingRuleImportSuggestionsMutation,
                    variables: { companyId: companyId(), direction },
                });
                return data?.postingRuleImportSuggestions.length ?? 0;
            },
        };
    });
    return context;
};
